/* ------------------------------------------------------------------
   MobileSwarm.tsx
------------------------------------------------------------------ */
import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { onClickHistogram } from '../../data/StatusStore';
import { useSwipeable } from 'react-swipeable';
import { d2gRound } from '../../components/utilities/Utilities';

/* ------------------------------------------------------------------
   Types
------------------------------------------------------------------ */
export interface DataItem {
  id: string;     // stable id (59 total)
  value: number;
  x?: number;
  y?: number;
}

export interface MobileSwarmProps {
  data: any;
  dataArray: DataItem[];
  histMax: number;
  histMin: number;
  histNA: number;
  histLength: number;
  chartId: string;
  indicator: any;
  colorClass: string;
  labelOnly: any;
  options: any;
  selectedId: string | null;
  currentIndex: number;
  setCurrentIndex: (i: number) => void;
  handleSwipeLeft: Function;
  handleSwipeRight: Function;
  setIndicatorByName: Function;
  descriptor: string;
  geoLabel: string;
  prevNodes: DataItem[];
  onLayoutReady: (nodes: DataItem[]) => void;
}

/* ------------------------------------------------------------------
   Constants
------------------------------------------------------------------ */
const FIRST_LOAD_DURATION = 750;   // set to 0 for no animation on first load

/* ------------------------------------------------------------------
   Helpers
------------------------------------------------------------------ */
const getQuintileColor = (v: number, min: number, max: number) =>
  d3.scaleQuantize<string>()
    .domain([min, max])
    .range(['#FFF2CC', '#F4C75E', '#E39B2F', '#C97815', '#8D4A04'])(v);

function layoutSwarm(
  data: DataItem[],
  min: number,
  max: number,
  w: number,
  h: number
) {
  const r   = w * 0.02;
  const pad = r * 0.6;
  const coll= r * 2 + pad;
  const L   = w * 0.08;
  const R   = w * 0.15;
  const cx  = L + (w - L - R) / 2;
  const ySc = d3.scaleLinear().domain([min, max]).range([h - 40, 40]);

  let last: DataItem | null = null;
  let dir = 1, run = 0;

  return data.map(d => {
    const y = ySc(d.value);
    let x   = cx;
    if (last && Math.abs((last.y ?? 0) - y) < coll) {
      x = cx + run * coll * dir * 1.5;
      if (dir === 1) run++;
      dir *= -1;
    } else { dir = 1; run = 0; last = { ...d, x, y }; }
    return { ...d, x, y };
  });
}

/* ------------------------------------------------------------------
   Component
------------------------------------------------------------------ */
const MobileSwarm: React.FC<MobileSwarmProps> = ({
  data,
  dataArray,
  histMax,
  histMin,
  chartId,
  indicator,
  selectedId,
  handleSwipeLeft,
  handleSwipeRight,
  setIndicatorByName,
  descriptor,
  geoLabel,
  prevNodes,
  onLayoutReady,
}) => {
  /* -------------- state & refs -------------- */
  const [nodes, setNodes] = useState<DataItem[]>(prevNodes);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [clickedId, setClickedId] = useState<string | null>(selectedId ?? null);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgSize, setSvgSize] = useState({ width: 350, height: 500 });

  /* -------------- swipe handlers -------------- */
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSwipeLeft(),
    onSwipedRight: () => handleSwipeRight(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  } as any);

  /* -------------- resize observer -------------- */
  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        setSvgSize({ width: w, height: w / 0.7 });
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  /* -------------- compute new layout -------------- */
  useEffect(() => {
    if (!svgSize.width) return;

    const newLayout = layoutSwarm(
      dataArray,
      histMin,
      histMax,
      svgSize.width,
      svgSize.height
    );
    onLayoutReady(newLayout);   // let parent cache it
    setNodes(newLayout);        // triggers transition
  }, [dataArray, svgSize, histMin, histMax]);

  /* -------------- sync click to store -------------- */
  useEffect(() => {
    if (clickedId) onClickHistogram(clickedId, "swarm", "mobile_swarm");
  }, [clickedId]);

  /* -------------- D3 join / animate -------------- */
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select<SVGSVGElement, unknown>(svgRef.current);
    const r   = svgSize.width * 0.02;

    const sel = svg
      .selectAll<SVGCircleElement, DataItem>('circle')
      .data(nodes, d => d.id);

    /* ----- ENTER ----- */
    sel.enter()
      .append('circle')
      .attr('r', r)
      .attr('cx', d =>
        prevNodes.length
          ? prevNodes.find(p => p.id === d.id)?.x ?? d.x ?? 0
          : 0
      )
      .attr('cy', d =>
        prevNodes.length
          ? prevNodes.find(p => p.id === d.id)?.y ?? d.y ?? 0
          : 0
      )
      .style('fill', d => getQuintileColor(d.value, histMin, histMax))
      .style('stroke', 'black')
      .style('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseenter', (_, d) => setHoveredId(d.id))
      .on('mouseleave', () => setHoveredId(null))
      .on('click',     (_, d) => setClickedId(d.id))
      .transition()
      .duration(prevNodes.length ? 750 : FIRST_LOAD_DURATION)
      .attr('cx', d => d.x ?? 0)
      .attr('cy', d => d.y ?? 0)
      .style('fill', d => getQuintileColor(d.value, histMin, histMax));

    /* ----- UPDATE ----- */
    sel.transition()
      .duration(750)
      .attr('cx', d => d.x ?? 0)
      .attr('cy', d => d.y ?? 0)
      .style('fill', d => getQuintileColor(d.value, histMin, histMax));

    /* ----- EXIT (shouldn’t happen) ----- */
    sel.exit().remove();

    /* ----- highlight strokes ----- */
    svg.selectAll<SVGCircleElement, DataItem>('circle').each(function (d) {
      const sel =
        (hoveredId && hoveredId === d.id) ||
        (clickedId && clickedId === d.id);
      d3.select(this)
        .style('stroke', sel ? (hoveredId === d.id ? '#E45756' : 'black') : 'black')
        .style('stroke-width', sel ? 6 : 1);
    });
  }, [nodes, hoveredId, clickedId, svgSize, histMin, histMax, prevNodes]);

  /* -------------- helper: tooltip -------------- */
  const renderTooltip = (id: string, isHover: boolean) => {
    const node = nodes.find(n => n.id === id);
    if (!node || node.x == null || node.y == null) return null;

    const R = svgSize.width * 0.15;
    const txtColor = isHover ? '#E45756' : 'black';

    return (
      <>
        <line
          x1={node.x}
          y1={node.y}
          x2={svgSize.width - R - 30}
          y2={node.y}
          stroke={txtColor}
          strokeWidth={1}
        />
        <rect
          x={svgSize.width - R - 30}
          y={node.y - 34}
          width={indicator.variable === 'median_personal_earnings' ? 120 : 60}
          height={25}
          fill="white"
        />
        <text
          x={svgSize.width - R - 25}
          y={node.y + 12.5}
          fontSize="24px"
          fontWeight="bold"
          fill={txtColor}
          textAnchor="start"
        >
          <tspan x={svgSize.width - R - 25} dy="-25px">
            {d2gRound(node.value, { DESCRIPTOR: descriptor, countDecimalOverride: 0 })}
          </tspan>
          <tspan
            x={svgSize.width - R - 25}
            dy="20px"
            fontSize="18px"
          >
            {geoLabel.split(' ').slice(1).join(' ')}
          </tspan>
          <tspan
            x={svgSize.width - R - 25}
            dy="20px"
            fontSize="18px"
          >
            {geoLabel.split(' ')[0]}
          </tspan>
        </text>
      </>
    );
  };

  /* -------------- render -------------- */
  const L = svgSize.width * 0.08;
  const yScale = d3
    .scaleLinear()
    .domain([histMin, histMax])
    .range([svgSize.height - 40, 40]);
  const yTicks = yScale.ticks(5);

  const padding = svgSize.width * 0.05; // new

  return (
    <div
      {...swipeHandlers}
      style={{ width: '100%', height: '100%', overflowX: 'hidden' }}
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={`-${padding} 0 ${svgSize.width + padding * 2} ${svgSize.height}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* --- Y‑axis --- */}
          <g>
            {yTicks.map((t, i) => (
              <g key={i}>
                <text
                  x={L - (indicator.variable === 'median_personal_earnings' ? -17.5 : 30)}
                  y={yScale(t) + 7.5}
                  fontSize={indicator.variable === 'median_personal_earnings' ? '18px' : '24px'}
                  textAnchor="end"
                >
                  {d2gRound(t, {
                    DESCRIPTOR:
                      descriptor === ''
                        ? 'X'
                        : indicator.variable === 'life_expectancy' ||
                          indicator.variable === 'ahdi'
                        ? 'X'
                        : descriptor,
                    countDecimalOverride: 0,
                  })}
                </text>
                <line
                  x1={L - (indicator.variable === 'median_personal_earnings' ? -22.5 : 20)}
                  y1={yScale(t)}
                  x2={L + (indicator.variable === 'median_personal_earnings' ? 27.5 : 0)}
                  y2={yScale(t)}
                  stroke="black"
                  strokeWidth={1}
                />
              </g>
            ))}
          </g>

          {/* --- tool‑tips --- */}
          {clickedId && renderTooltip(clickedId, false)}
          {hoveredId && renderTooltip(hoveredId, true)}
        </svg>

        {/* description unchanged */}
        <div className="description">
          The{' '}
          <span style={{ cursor: 'pointer' }} onClick={() => setIndicatorByName('ahdi')}>
            Human Development Index
          </span>{' '}
          is a single indicator of well‑being based on:{' '}
          <span style={{ cursor: 'pointer' }} onClick={() => setIndicatorByName('life_expectancy')}>
            Life Expectancy
          </span>
          ,{' '}
          <span style={{ cursor: 'pointer' }} onClick={() => setIndicatorByName('edu_index')}>
            Educational Attainment
          </span>
          , and <span>Income</span>
        </div>
      </div>
    </div>
  );
};

export default MobileSwarm;
